import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

import ContactSelectorModal from '@/components/Modals/ContactSelectorModal';
import TemplateSelectorModal from '@/components/Modals/TemplateSelectorModal';

const EditCampaignModal = ({
    isOpen,
    onClose,
    campaignId,
    onSave,
    templates,
    organizations,
}) => {
    const [campaignData, setCampaignData] = useState(null);
    const [scenarios, setScenarios] = useState([]);

    const [showContactModal, setShowContactModal] = useState(false);
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [orgSearchTerm, setOrgSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);

    // 🔹 selections per step
    const [selectedByStep, setSelectedByStep] = useState({}); // { "1": templateId, "2": templateId, ... }

    useEffect(() => {
        if (!isOpen || !campaignId) return;
        api.get(`/api/campaigns/${campaignId}/`)
            .then(res => {
                const d = res.data || {};
                setCampaignData({
                    name: d.name || '',
                    scenario: d.scenario || '',
                    frequency: d.frequency_days ?? 0,
                    stagger: d.stagger_sends ? 'yes' : 'no',
                    selectedContacts: Array.isArray(d.contacts) ? d.contacts.map(c => c.id) : [],
                    status: d.status || 'draft',
                });

                // init template_map (backend should return it; fallback to empty)
                const mapFromApi = d.template_map && typeof d.template_map === 'object' ? d.template_map : {};
                setSelectedByStep(mapFromApi);
            })
            .catch(err => console.error('Failed to load campaign:', err));
    }, [isOpen, campaignId]);

    useEffect(() => {
        api.get('/api/campaigns/scenarios/')
            .then(res => setScenarios(res.data || []))
            .catch(err => console.error('Failed to load scenarios:', err));
    }, []);

    const handleScenarioChange = useCallback((value) => {
        setCampaignData(prev => ({ ...prev, scenario: value }));
        setSelectedByStep({}); // reset selections when scenario changes
    }, []);

    const handleStaggerChange = useCallback((value) => {
        setCampaignData(prev => ({ ...prev, stagger: value }));
    }, []);

    const selectedSummary = useMemo(() => {
        const steps = Object.keys(selectedByStep).sort((a, b) => Number(a) - Number(b));
        if (steps.length === 0) return 'Choose templates';
        return steps.map(step => `Step ${step}: ${selectedByStep[step]}`).join('  •  ');
    }, [selectedByStep]);

    const handleUpdate = async () => {
        if (!campaignData) return;
        setLoading(true);
        try {
            await api.put(`/api/campaigns/${campaignId}/update/`, {
                name: campaignData.name,
                scenario: campaignData.scenario,
                contact_ids: campaignData.selectedContacts,
                frequency_days: Number(campaignData.frequency),
                stagger_sends: campaignData.stagger === 'yes',
                status: campaignData.status,
                // 🔹 pass selected templates per step
                template_map: selectedByStep,
            });
            onSave?.();
            onClose();
        } catch (err) {
            console.error('❌ Update failed:', err);
            alert('Failed to update campaign. Please check fields.');
        } finally {
            setLoading(false);
        }
    };

    if (!campaignData) return null;

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>Edit Campaign</DialogTitle>
                    <DialogDescription>Modify your campaign settings.</DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div>
                        <Label className="mb-3">Campaign Name</Label>
                        <Input
                            value={campaignData.name}
                            onChange={(e) => setCampaignData(prev => ({ ...prev, name: e.target.value }))}
                        />
                    </div>

                    <div>
                        <Label className="mb-3">Scenario</Label>
                        <Select value={campaignData.scenario} onValueChange={handleScenarioChange}>
                            <SelectTrigger>
                                <SelectValue placeholder="Select scenario" />
                            </SelectTrigger>
                            <SelectContent>
                                {scenarios.map((s, i) => (
                                    <SelectItem key={i} value={s.value}>{s.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div>
                        <Label className="mb-3">Status</Label>
                        <Select
                            value={campaignData.status}
                            onValueChange={(value) => setCampaignData(prev => ({ ...prev, status: value }))}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="draft">Draft</SelectItem>
                                <SelectItem value="active">Active</SelectItem>
                                <SelectItem value="paused">Paused</SelectItem>
                                <SelectItem value="waiting">Waiting for approval</SelectItem>
                                <SelectItem value="completed">Completed</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    <div>
                        <Label className="mb-3">Contacts</Label>
                        <Button variant="outline" className="w-full" onClick={() => setShowContactModal(true)}>
                            {campaignData.selectedContacts?.length > 0
                                ? `${campaignData.selectedContacts.length} Contacts Selected`
                                : 'Select Contacts'}
                        </Button>
                    </div>

                    <div>
                        <Label className="mb-3">Select Templates Per Step</Label>
                        <Button
                            variant="outline"
                            className="w-full justify-between"
                            onClick={() => setShowTemplateModal(true)}
                        >
                            <span>{selectedSummary}</span>
                        </Button>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <Label className="mb-3">Frequency (days)</Label>
                            <Input
                                type="number"
                                min={0}
                                value={campaignData.frequency ?? 0}
                                onChange={(e) =>
                                setCampaignData(prev => ({ ...prev, frequency: Number(e.target.value ?? 0) }))}
                            />
                        </div>
                        <div>
                            <Label className="mb-3">Stagger Sends</Label>
                            <Select value={campaignData.stagger} onValueChange={handleStaggerChange}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="yes">Yes</SelectItem>
                                    <SelectItem value="no">No</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>

                <div className="flex justify-end space-x-2 pt-4">
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={handleUpdate} disabled={loading}>
                        {loading ? 'Saving...' : 'Save Changes'}
                    </Button>
                </div>
            </DialogContent>

            {/* Contacts */}
            <ContactSelectorModal
                isOpen={showContactModal}
                onClose={() => setShowContactModal(false)}
                organizations={organizations}
                newCampaign={campaignData}
                setNewCampaign={setCampaignData}
                orgSearchTerm={orgSearchTerm}
                setOrgSearchTerm={setOrgSearchTerm}
            />

            {/* Templates-per-step */}
            <TemplateSelectorModal
                isOpen={showTemplateModal}
                onClose={() => setShowTemplateModal(false)}
                templates={templates}
                newCampaign={campaignData}
                selectedByStep={selectedByStep}
                setSelectedByStep={setSelectedByStep}
                scenarios={scenarios}
            />
        </Dialog>
    );
};

export default EditCampaignModal;